import { httpsCallable } from 'firebase/functions'
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore'
import { functions } from '@/lib/firebase/functions'
import { db } from '@/lib/firebase/firestore'
import type {
  ProgramDraft,
  ProgramWarning,
  PublicProgram,
  SourceSubmission,
} from '../../../../functions/src/features/digitalPrograms/program.domain'
import type { VoteStatus, VoteReceipt, VoteResults } from '../audienceAward/programVoting'
export type { ProgramDraft, ProgramWarning, PublicProgram }
export {
  ITEM_FIELDS,
  PIECE_TYPES,
  effectiveValues,
  showValues,
  publicSnapshot,
  validateDraft,
} from '../../../../functions/src/features/digitalPrograms/program.domain'
export type {
  PieceValues,
  ItemField,
  FieldMapping,
  ProgramSettings,
  ProgramItem,
  ProgramImage,
} from '../../../../functions/src/features/digitalPrograms/program.domain'
async function call<T>(name: string, data: Record<string, unknown>): Promise<T> {
  return (await httpsCallable<Record<string, unknown>, T>(functions, name)(data)).data
}
export const programApi = {
  create: (data: Record<string, unknown>) => call<ProgramDraft>('createDigitalProgram', data),
  getAdmin: (id: string) =>
    call<{ draft: ProgramDraft; validation: ProgramWarning[]; published: PublicProgram | null }>(
      'getDigitalProgramAdmin',
      { id }
    ),
  edit: (id: string, revision: number, data: Record<string, unknown>) =>
    call<ProgramDraft>('editDigitalProgram', { ...data, id, revision }),
  publish: (id: string, revision: number) =>
    call<{ version: number; unchanged: boolean }>('publishDigitalProgram', { id, revision }),
  unpublish: (id: string) => call<{ unpublished: boolean }>('unpublishDigitalProgram', { id }),
  asset: (id: string, kind: string, preview: boolean) =>
    call<{ base64: string; mimeType: string; filename: string }>('getDigitalProgramAsset', {
      id,
      kind,
      preview,
    }),
  status: (awardId: string, ballotToken: string) =>
    call<VoteStatus>('getAudienceAwardStatus', { awardId, ballotToken }),
  vote: (awardId: string, ballotToken: string, itemId: string) =>
    call<VoteReceipt>('submitAudienceAwardVote', { awardId, ballotToken, itemId }),
  results: (awardId: string) => call<VoteResults>('getAudienceAwardResults', { awardId }),
  setOpen: (awardId: string, isOpen: boolean) =>
    call<{ isOpen: boolean }>('setAudienceAwardVotingOpen', { awardId, isOpen }),
  async public(slug: string) {
    const snapshot = await getDoc(doc(db, 'publicPrograms', slug))
    return snapshot.exists() ? (snapshot.data() as PublicProgram) : null
  },
  async forProduction(productionId: string) {
    const result = await getDocs(
      query(collection(db, 'digitalPrograms'), where('productionId', '==', productionId))
    )
    return result.docs.map((d) => d.data() as ProgramDraft)
  },
  async publicForEvent(eventId: string) {
    const result = await getDocs(
      query(collection(db, 'publicPrograms'), where('eventId', '==', eventId))
    )
    return result.docs.map((d) => d.data() as PublicProgram)
  },
  async fields(productionId: string) {
    const production = await getDoc(doc(db, 'productions', productionId))
    const fields: Array<{
      id: string
      label: string
      libraryId?: string
      source: 'intake' | 'technical'
    }> = []
    for (const source of ['intake', 'technical'] as const) {
      const appId =
        production.data()?.[source === 'intake' ? 'intakeApplicationId' : 'techApplicationId']
      if (!appId) continue
      const forms = await getDocs(
        query(collection(db, 'applicationForms'), where('applicationId', '==', appId))
      )
      for (const form of forms.docs)
        for (const section of form.data().sections ?? [])
          for (const field of section.fields ?? []) {
            if (!fields.some((f) => f.id === field.id && f.source === source))
              fields.push({
                id: field.id,
                label: field.label,
                ...(field.fieldLibraryId ? { libraryId: field.fieldLibraryId } : {}),
                source,
              })
          }
    }
    return fields
  },
  async events(productionId: string) {
    const result = await getDocs(
      query(collection(db, 'events'), where('productionId', '==', productionId))
    )
    return result.docs.map((d) => ({
      id: d.id,
      title: String(d.data().title),
      slug: String(d.data().slug),
    }))
  },
  async submissions(productionId: string) {
    const result = await getDocs(
      query(collection(db, 'applicationSubmissions'), where('productionId', '==', productionId))
    )
    return result.docs.map((d) => ({ ...d.data(), id: d.id }) as SourceSubmission)
  },
}
export function downloadAsset(asset: { base64: string; mimeType: string; filename: string }) {
  const bytes = Uint8Array.from(atob(asset.base64), (c) => c.charCodeAt(0))
  const url = URL.createObjectURL(new Blob([bytes], { type: asset.mimeType }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = asset.filename
  anchor.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
